import LoadingButton from "@mui/lab/LoadingButton";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
	GridCellParams,
	GridColDef,
	GridRowId,
	GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { TimeLabel } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";

import { useAllTimeLabelsDataSource } from "../datasources/AllTimeLabelsDataSource";
import List from "../lists/List";

export default function DialogSelectTimeLabels(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		setTimeLabels: (value: TimeLabel[]) => void;
		timeLabels: TimeLabel[];
		contactId: string;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);

	const [timeLabels, setTimeLabels] = useState<TimeLabel[]>([]);
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const allTimeLabelsDataSource = useAllTimeLabelsDataSource();

	useEffect(() => {
		setOpen(props.open);
		if (props.open) {
			allTimeLabelsDataSource.fetchDataForId?.(props.contactId);
		}
	}, [props.open, props.contactId]);

	useEffect(() => {
		if (allTimeLabelsDataSource.rows) {
			setTimeLabels(allTimeLabelsDataSource.rows);
		}
	}, [allTimeLabelsDataSource.rows]);

	useEffect(() => {
		if (!props.timeLabels) return;
		const foundMemberIds: string[] = [];
		props.timeLabels?.forEach((e: TimeLabel) => {
			if (e.id) {
				foundMemberIds.push(e.id);
			}
		});
		setRowSelectionModel(foundMemberIds);
	}, [props.timeLabels]);

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setSaveClicked(false);
		setRowSelectionModel([]);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();
		setSaveClicked(true);

		const selectedMembers = timeLabels.filter((label: TimeLabel) =>
			rowSelectionModel.includes(label.id),
		);

		props.setTimeLabels(selectedMembers);

		handleClose();
	};

	const columns: GridColDef<TimeLabel>[] = [
		{
			field: "name",
			headerName: t("name"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.name;
			},
		},
		{
			field: "isActive",
			headerName: t("timetracking.timeLabels.tableTimeLabel.isActive"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.isActive ? (
					<span style={{ color: "green" }}>{t("yes")}</span>
				) : (
					<span style={{ color: "red" }}>{t("no")}</span>
				);
			},
		},
	];

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="md"
			fullWidth={true}>
			<DialogTitle>{t("timetracking.timeLabels.dialogTimeLabel.title")}</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						height: "auto",
						width: "100%",
						borderBottom: "1px solid lightgrey",
					}}>
					<List
						dataSource={allTimeLabelsDataSource}
						columns={columns as GridCellParams[]}
						rowSelectionModel={rowSelectionModel as unknown as GridRowSelectionModel}
						onRowSelectionModelChange={(newRowSelectionModel) => {
							setRowSelectionModel((prev) => {
								const filteredIds = allTimeLabelsDataSource.rows.map((row) => row.id);
								const notVisibleSelectedIds = prev.filter(
									(id) => !filteredIds.includes(id as string),
								);
								const newlySelectedIds = (newRowSelectionModel as unknown as string[]).filter(
									(id) => filteredIds.includes(id as string),
								);
								return [...notVisibleSelectedIds, ...newlySelectedIds];
							});
						}}
						contactId={props.contactId}>
						{/* <Button
							data-testid="add-button"
							onClick={() => {
								setOpen(true);
							}}
							className="blueButton marginLeft20"
						>
							{t("timetracking.timeLabels.dialogTimeLabels.add")}
						</Button> */}
					</List>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					data-testid="dialog-submit-team-member"
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
